/**
 * Compacting › «Simulazione» — the pure half (owner rulings 2026-11-02; platform ADR 9056).
 *
 * A simulation of the one move run is the market's daily run asked again today, over ONE run scope
 * picked from the market's library (Management › Perimetri) and under an overlay of the parameters the
 * platform admits for it (`GET /compacting/simulations/overlay-keys`). The platform solves it as its own
 * optimisation — a smaller scope is not a filter on a bigger run — and writes a move run that no live
 * read lists and on which nothing can be decided. Its report and its moves are read through the same
 * `/compacting/runs/{id}…` reads as the live run's, by `moveRunId`.
 *
 * Everything decided in the browser is decided here, on strings, and tested:
 *
 * - **the URL is the question**: `?market=` (never defaulted but by ADR 9056, the only market the person
 *   sees), `?moveSimulation=` (the simulation launched from the composer: the form stays, read-only while
 *   it runs) and `?moveSimulationFrom=` («Modifica e rilancia»: the form filled from a stored question,
 *   relaunched from it). Their own keys, never in-season Simulate's `?simulation=`, which the address bar
 *   may still carry from another page;
 * - **the scope** is sent only when it is not the daily key's (`scopeOfChoice`): no scope means "the
 *   daily scope as it would resolve now", which is the default the page opens on;
 * - **the overlay** is the admitted keys the planner moved, as the register's draft check sends them;
 *   overlaying a key replaces it at EVERY scope it holds (per-store values flattened), and the page says so;
 * - **polling** stops on a terminal state, and a state this build does not know is terminal too.
 */

import { ApiProblemError, callOperation } from '@/api';
import { listMoveRunSimulations } from '@/api/generated';
import type { MoveRunKpi, MoveRunSimulationView, MoveRunSummary, OverlayKeyView, ParameterRow } from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion } from '@/export';
import type { ExportCell, ExportRequest } from '@/export';
import { PERSON_NAME_DISPLAY, personExportCell } from '@/planning';

import type { ExportScope } from '../../manifest';
import { catalogueWords } from '../export-drivers';
import { isInFlight, simulationStateKey, UNRECOGNISED_STATE_KEY } from '../simulate';
import { totalOf } from './move-run';

// ── the URL ──────────────────────────────────────────────────────────────────

/** The composer: market, run scope, overlay. */
export const COMPACTING_SIMULATION_PATH = '/inseason/compacting-simulation';
/** The market's simulations, newest first — the composer's other tab. A static segment: it wins over `:simulationId`. */
export const COMPACTING_SIMULATION_HISTORY_PATH = `${COMPACTING_SIMULATION_PATH}/history`;
/** The market a simulation is asked for — the same key «Perimetri» reads, so the library link keeps it. */
export const MARKET_PARAM = 'market';
/** The simulation launched from the composer. */
export const MOVE_SIMULATION_PARAM = 'moveSimulation';
/** The simulation whose question fills the composer, relaunched from it («Modifica e rilancia»). */
export const MOVE_SIMULATION_FROM_PARAM = 'moveSimulationFrom';

export interface CompactingSimulationScope {
  readonly market: string | null;
  readonly simulationId: string | null;
  readonly fromId: string | null;
}

function text(params: URLSearchParams, name: string): string | null {
  const value = params.get(name)?.trim() ?? '';
  return value === '' ? null : value;
}

/** The composer's question from a search string. Pure. */
export function readCompactingSimulationScope(search: string): CompactingSimulationScope {
  const params = new URLSearchParams(search);
  return { market: text(params, MARKET_PARAM), simulationId: text(params, MOVE_SIMULATION_PARAM), fromId: text(params, MOVE_SIMULATION_FROM_PARAM) };
}

/** One simulation's page. */
export function compactingSimulationPath(simulationId: string): string {
  return `${COMPACTING_SIMULATION_PATH}/${encodeURIComponent(simulationId)}`;
}

/** The simulation a pathname names — `/inseason/compacting-simulation/<id>` — or `null`; `history` names none. Pure. */
export function readCompactingSimulationPath(pathname: string): string | null {
  const prefix = `${COMPACTING_SIMULATION_PATH}/`;
  if (!pathname.startsWith(prefix)) return null;
  const rest = pathname.slice(prefix.length);
  if (rest === '' || rest.includes('/')) return null;
  const id = decodeURIComponent(rest);
  return id === 'history' ? null : id;
}

/** The market alone from a search string — what the history reads. Pure. */
export function readMarketParam(search: string): string | null {
  return text(new URLSearchParams(search), MARKET_PARAM);
}

/*
 * The two links below open the COMPOSER: they write the composer's keys, which the page that draws the
 * link does not read. They spell the keys as literals (`market`, `moveSimulationFrom` — the constants
 * above) so that the URL-parameter census (ADR 9054), which counts a written constant as a read, does not
 * give the report or a simulation's page a chip for a key of another page.
 */

/** The composer filled from a simulation, on its market: «Modifica e rilancia». */
export function relaunchHref(simulation: Pick<MoveRunSimulationView, 'simulationId' | 'countryKey'>): string {
  const query = new URLSearchParams({ market: simulation.countryKey, moveSimulationFrom: simulation.simulationId });
  return `${COMPACTING_SIMULATION_PATH}?${query.toString()}`;
}

/** The composer on a market — what the report's link opens. */
export function composeHref(market: string | null): string {
  return market === null ? COMPACTING_SIMULATION_PATH : `${COMPACTING_SIMULATION_PATH}?${new URLSearchParams({ market }).toString()}`;
}

// ── the run scope ────────────────────────────────────────────────────────────

/** The `compacting.run_scope` key: the Scope the market's daily run is posed over. */
export const RUN_SCOPE_KEY = 'compacting.run_scope';
/** The system Scope a market with no `compacting.run_scope` row runs over: every store and article. */
export const WHOLE_MARKET_SCOPE = 'default';

/** The daily key's Scope: the row in force, or the whole market when the key is unset. Pure. */
export function dailyScopeId(textValue: string | null | undefined, source: 'row' | 'default' | 'unset' | undefined): { readonly scopeId: string; readonly unset: boolean } | null {
  if (source === undefined) return null;
  const value = textValue?.trim() ?? '';
  if (source === 'row' && value !== '') return { scopeId: value, unset: false };
  return { scopeId: WHOLE_MARKET_SCOPE, unset: true };
}

/**
 * The request's `scope`: nothing for the daily key's own (`null` choice, or the very Scope the key
 * names), the Scope by id otherwise — pinned at its newest version when asked. Pure.
 */
export function scopeOfChoice(choice: string | null, daily: string | null): { readonly scope?: { readonly scopeId: string } } {
  return choice === null || choice === daily ? {} : { scope: { scopeId: choice } };
}

/** Each source of a simulation's Scope (`MoveRunSimulationScopeView.source`), in words. */
export const SIMULATION_SCOPE_SOURCE_KEY: Readonly<Record<string, string>> = {
  request: 'inseason.compactingSimulation.scopeSource.request',
  daily_key: 'inseason.compactingSimulation.scopeSource.dailyKey',
  whole_market: 'inseason.compactingSimulation.scopeSource.wholeMarket',
};

/** Each source of a run's Scope (`MoveRunScopeView.source`), in words. */
export const RUN_SCOPE_SOURCE_KEY: Readonly<Record<string, string>> = {
  registry: 'inseason.compacting.runScope.source.registry',
  unset_whole_market: 'inseason.compacting.runScope.source.unsetWholeMarket',
  simulation: 'inseason.compacting.runScope.source.simulation',
};

// ── the overlay ──────────────────────────────────────────────────────────────

/** Whether a key's grant needs the owner's capability (`move_run_simulation_valuation_overlay`). Pure. */
export function isOwnerKey(key: Pick<OverlayKeyView, 'grant'>): boolean {
  return key.grant === 'owner';
}

/**
 * The registry rows that hold a key for this market: an estate row, or a row of this market. A row of
 * another market is not what this market's run reads. Pure.
 */
export function marketRows(rows: readonly ParameterRow[], market: string): readonly ParameterRow[] {
  return rows.filter((row) => {
    const country = (row.scope ?? []).find((pair) => pair.dimension === 'country');
    if (country !== undefined) return country.value === market;
    return row.scopeDimension !== 'country' || row.scopeValue === market;
  });
}

/** A key with no row in force at the base, which the platform admits only when `seatable`. Pure. */
export function needsRowInForce(key: Pick<OverlayKeyView, 'seatable'>, rows: readonly ParameterRow[]): boolean {
  return !key.seatable && rows.length === 0;
}

// ── refusals ─────────────────────────────────────────────────────────────────

export const SCOPE_REFUSED_CODE = 'planning.compacting.simulation_scope_refused';
export const OVERLAY_KEY_REFUSED_CODE = 'planning.compacting.simulation_overlay_key_refused';
export const CYCLE_WINDOW_CODE = 'planning.compacting.simulation_cycle_window';
export const CAPACITY_CODE = 'planning.compacting.simulation_capacity_exhausted';
export const NOT_FOUND_CODE = 'planning.compacting.simulation_not_found';

export type CompactingSimulationRefusal =
  | { readonly kind: 'scope'; readonly reason: string; readonly scopeId: string }
  | { readonly kind: 'overlayKey'; readonly parameterKey: string; readonly reason: string }
  | { readonly kind: 'cycleWindow'; readonly cycle: string; readonly tryAfter: string | null }
  | { readonly kind: 'capacity' }
  | { readonly kind: 'notFound' };

/** A launch or read the platform refused by one of the simulation's own codes, or `null`. Pure. */
export function compactingSimulationRefusal(error: unknown): CompactingSimulationRefusal | null {
  if (!(error instanceof ApiProblemError)) return null;
  const problem = error.problem;
  const word = (name: string): string => (typeof problem[name] === 'string' ? problem[name] as string : '');
  switch (problem.code) {
    case SCOPE_REFUSED_CODE: return { kind: 'scope', reason: word('reason'), scopeId: word('scopeId') };
    case OVERLAY_KEY_REFUSED_CODE: return { kind: 'overlayKey', parameterKey: word('parameterKey'), reason: word('reason') };
    case CYCLE_WINDOW_CODE: {
      const tryAfter = word('tryAfter');
      return { kind: 'cycleWindow', cycle: word('cycle'), tryAfter: tryAfter !== '' && !Number.isNaN(Date.parse(tryAfter)) ? tryAfter : null };
    }
    case CAPACITY_CODE: return { kind: 'capacity' };
    case NOT_FOUND_CODE: return { kind: 'notFound' };
    default: return null;
  }
}

/** Why an overlay key is refused (`OverlayKeysView.refused`, the overlay refusal's `reason`), in words. */
export const OVERLAY_KEY_REASON_KEY: Readonly<Record<string, string>> = {
  scope_is_a_request_field: 'inseason.compactingSimulation.overlayReason.scopeIsARequestField',
  outside_stage_section: 'inseason.compactingSimulation.overlayReason.outsideStageSection',
  key_unknown: 'inseason.compactingSimulation.overlayReason.keyUnknown',
  not_in_force: 'inseason.compactingSimulation.overlayReason.notInForce',
  value_invalid: 'inseason.compactingSimulation.overlayReason.valueInvalid',
  outside_band: 'inseason.compactingSimulation.overlayReason.outsideBand',
  not_in_vocabulary: 'inseason.compactingSimulation.overlayReason.notInVocabulary',
  grant_missing: 'inseason.compactingSimulation.overlayReason.grantMissing',
};

export function overlayReasonKey(reason: string): string {
  return OVERLAY_KEY_REASON_KEY[reason] ?? 'inseason.compactingSimulation.overlayReason.unstated';
}

// ── the state ────────────────────────────────────────────────────────────────

/** The view's state: `state` where the platform sends it, else `status`, which repeats it. Pure. */
export function stateOf(view: Pick<MoveRunSimulationView, 'state' | 'status'>): string {
  return view.state ?? view.status;
}

/** Whether the simulation still moves — re-read while it does. */
export function inFlight(view: Pick<MoveRunSimulationView, 'state' | 'status'>): boolean {
  return isInFlight(stateOf(view));
}

/** Whether the simulation wrote a move run to open. */
export function wroteRun(view: Pick<MoveRunSimulationView, 'moveRunId'>): boolean {
  return view.moveRunId !== null && view.moveRunId !== undefined && view.moveRunId !== '';
}

/** The overlay as `key=value; …`, keys in order — what tells two simulations apart. Pure. */
export function overlayText(overlay: Readonly<Record<string, string>>): string {
  return Object.entries(overlay).sort(([a], [b]) => a.localeCompare(b)).map(([key, value]) => `${key}=${value}`).join('; ');
}

// ── the compare ──────────────────────────────────────────────────────────────

/**
 * The live run of the simulation's day: the same market and stock day (`as_of`), completed, the one not
 * superseded first — from the live directory's first page. `null` when none is listed. Pure.
 */
export function liveRunOfDay(runs: readonly MoveRunSummary[], simulated: Pick<MoveRunSummary, 'country_key' | 'as_of'>): MoveRunSummary | null {
  const same = runs.filter((run) => run.simulation_id === null && run.country_key === simulated.country_key && run.as_of === simulated.as_of);
  return same.find((run) => !run.superseded && run.status !== 'refused') ?? same.find((run) => run.status !== 'refused') ?? same[0] ?? null;
}

export const COMPARE_FIGURES = ['linesProposed', 'linesHeld', 'refused', 'unitsProposed', 'netProposed', 'netHeld'] as const;
export type CompareFigure = (typeof COMPARE_FIGURES)[number];

/** Each figure of a KPI block the compare lines up, as a number (totals are always numbers). Pure. */
export function compareFigure(kpi: MoveRunKpi, figure: CompareFigure): number {
  switch (figure) {
    case 'linesProposed': return totalOf(kpi.lines_proposed);
    case 'linesHeld': return totalOf(kpi.lines_held);
    case 'refused': return totalOf(kpi.candidates_refused);
    case 'unitsProposed': return totalOf(kpi.units_proposed);
    case 'netProposed': return totalOf(kpi.net_benefit_proposed);
    case 'netHeld': return totalOf(kpi.net_benefit_held);
  }
}

/** Money figures, drawn in the run's currency. */
export function isMoneyFigure(figure: CompareFigure): boolean {
  return figure === 'netProposed' || figure === 'netHeld';
}

export const COMPARE_FIGURE_KEY: Readonly<Record<CompareFigure, string>> = {
  linesProposed: 'inseason.compacting.report.kpi.linesProposed',
  linesHeld: 'inseason.compacting.report.kpi.linesHeld',
  refused: 'inseason.compacting.report.kpi.refused',
  unitsProposed: 'inseason.compacting.report.kpi.unitsProposed',
  netProposed: 'inseason.compacting.report.kpi.netProposed',
  netHeld: 'inseason.compacting.report.kpi.netHeld',
};

// ── the history export ───────────────────────────────────────────────────────

const PLATFORM_VERSION = buildVersion();
const stateWords = (code: string): string | null => {
  const key = simulationStateKey(code);
  return key === UNRECOGNISED_STATE_KEY ? null : key;
};

/** The history table as a file — the rows given, in the order given. */
export function compactingSimulationListRequest(items: readonly MoveRunSimulationView[], market: string | null): ExportRequest {
  const missing: ExportCell = { kind: 'missing', state: 'not-ranged' };
  const cells: ExportCell[][] = items.map((row) => [
    { kind: 'text', value: row.simulationId },
    row.requestedAt === null || row.requestedAt === undefined ? missing : { kind: 'text', value: row.requestedAt },
    { kind: 'label', value: stateOf(row), code: stateOf(row) },
    row.scope === null || row.scope === undefined ? missing : { kind: 'text', value: row.scope.scopeId },
    row.scope === null || row.scope === undefined ? missing : { kind: 'number', raw: row.scope.version },
    { kind: 'number', raw: Object.keys(row.overlay).length },
    { kind: 'text', value: overlayText(row.overlay) },
    personExportCell(row.requestedBy, row.requestedByName, row.requestedByNameAbsentReason),
    row.moveRunId === null || row.moveRunId === undefined ? missing : { kind: 'text', value: row.moveRunId },
    row.relaunchedFrom === null || row.relaunchedFrom === undefined ? missing : { kind: 'text', value: row.relaunchedFrom },
  ]);
  const filters = [market === null ? { field: 'country_key', operator: 'unset', value: '' } : { field: 'country_key', operator: 'equals', value: market }];
  return {
    id: COMPACTING_SIMULATION_HISTORY_EXPORT_ID,
    titleKey: 'inseason.compactingSimulation.history.title',
    columns: [
      { key: 'simulation_id', headerKey: 'inseason.compactingSimulation.column.simulation', descriptionKey: 'inseason.columnGuide.compactingSimulation.simulation', kind: 'text' },
      { key: 'requested_at', headerKey: 'inseason.compactingSimulation.column.requestedAt', descriptionKey: 'inseason.columnGuide.compactingSimulation.requestedAt', kind: 'text' },
      { key: 'state', headerKey: 'inseason.compactingSimulation.column.state', descriptionKey: 'inseason.columnGuide.compactingSimulation.state', kind: 'label', display: catalogueWords(stateWords) },
      { key: 'scope_id', headerKey: 'inseason.compactingSimulation.column.scope', descriptionKey: 'inseason.columnGuide.compactingSimulation.scope', kind: 'text' },
      { key: 'scope_version', headerKey: 'inseason.compactingSimulation.column.scopeVersion', descriptionKey: 'inseason.columnGuide.compactingSimulation.scopeVersion', kind: 'number' },
      { key: 'overlay_keys', headerKey: 'inseason.compactingSimulation.column.keys', descriptionKey: 'inseason.columnGuide.compactingSimulation.keys', kind: 'number' },
      { key: 'overlay', headerKey: 'inseason.compactingSimulation.column.overlay', descriptionKey: 'inseason.columnGuide.compactingSimulation.overlay', kind: 'text' },
      { key: 'requested_by_name', headerKey: 'inseason.compactingSimulation.column.by', descriptionKey: 'inseason.columnGuide.compactingSimulation.by', kind: 'label', display: PERSON_NAME_DISPLAY },
      {
        key: 'move_run_id', headerKey: 'inseason.compactingSimulation.column.moveRun', descriptionKey: 'inseason.columnGuide.compactingSimulation.moveRun', kind: 'text',
        absent: { notRanged: 'reason', reasonKeys: { 'not-ranged': 'inseason.compactingSimulation.absent.noRun' } },
      },
      {
        key: 'relaunched_from', headerKey: 'inseason.compactingSimulation.column.relaunchedFrom', descriptionKey: 'inseason.columnGuide.compactingSimulation.relaunchedFrom', kind: 'text',
        absent: { notRanged: 'reason', reasonKeys: { 'not-ranged': 'inseason.compactingSimulation.absent.notRelaunched' } },
      },
    ],
    rows: () => cells,
    rowCount: cells.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{
        measure: 'move_run_simulation',
        definition: 'One simulation of the market’s one move run, asked today over one run scope of the market’s library under an overlay of admitted parameters (bata-platform ADR 9056): when it was asked, its state, the scope and version it was pinned at, the overlay, who asked, the move run it wrote and the simulation it was relaunched from.',
        registerVersion: '',
      }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: PLATFORM_VERSION,
    },
    perimeter: {
      filters,
      sort: [{ key: 'requested_at', direction: 'desc' }],
      visibleColumns: ['simulation_id', 'requested_at', 'state', 'scope_id', 'scope_version', 'overlay_keys', 'overlay', 'requested_by_name', 'move_run_id', 'relaunched_from'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

export const COMPACTING_SIMULATION_HISTORY_EXPORT_ID = 'inseason.compactingSimulation.history';

/** The history route's registered export: the market on the URL's simulations. No market, no read. */
export async function compactingSimulationListExport(scope?: ExportScope): Promise<ExportRequest> {
  const search = scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const { market } = readCompactingSimulationScope(search);
  const items = market === null ? [] : (await callOperation(listMoveRunSimulations, { query: { country_key: market, limit: 100 } })).items;
  return compactingSimulationListRequest(items, market);
}

// ── gate D2 ──────────────────────────────────────────────────────────────────

/** The share test of a gate D2 verdict, as numbers: `null` when either share is not stated. Pure. */
export function gateD2Test(view: { readonly clearingShare?: string | null; readonly minimumClearingShare?: string | null }):
  { readonly share: number; readonly minimum: number; readonly passes: boolean } | null {
  const share = view.clearingShare === null || view.clearingShare === undefined || view.clearingShare.trim() === '' ? NaN : Number(view.clearingShare);
  const minimum = view.minimumClearingShare === null || view.minimumClearingShare === undefined || view.minimumClearingShare.trim() === '' ? NaN : Number(view.minimumClearingShare);
  if (!Number.isFinite(share) || !Number.isFinite(minimum)) return null;
  return { share, minimum, passes: share >= minimum };
}

/** Why a simulation's gate D2 verdict was or was not re-checked (`MoveRunSimulationGateD2View.reason`), in words. */
export const GATE_D2_REASON_KEY: Readonly<Record<string, string>> = {
  overlay_names_gate_d2_input: 'inseason.compactingSimulation.gateD2.reason.overlayNamesInput',
  overlay_names_no_gate_d2_input: 'inseason.compactingSimulation.gateD2.reason.overlayNamesNoInput',
  gate_d2_inputs_unavailable: 'inseason.compactingSimulation.gateD2.reason.inputsUnavailable',
};
